//LIBRARIES
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { change } from 'redux-form';
import { FaEnvelope } from 'react-icons/fa';
import { localize } from 'react-localize-redux';
import { MdWarning as WarningIcon } from 'react-icons/md';

import { MuiThemeProvider } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import Button from '@material-ui/core/Button';
import { withStyles } from '@material-ui/core/styles';

//HELPERS
import { AccentColor } from 'helpers/AccentColorHelper';
import { muiThemeNext } from 'helpers/MUIFiles';
import { deepDiver } from 'helpers/utilities';
import { drawerStyles } from 'helpers/StyleHelper';
import { titleize } from 'helpers/Formatter';
import { getTranslatedString } from 'helpers/i18n';
import { colors } from 'helpers/ColorHelper';
import { featureEnabled } from 'helpers/ToggleBot';

//COMPONENTS
import AvailabilitiesForm from 'components/landlord_app/community/AvailabilitiesForm/AvailabilitiesForm';
import ConfirmationDialog from 'components/shared/ConfirmationDialog';
import { UserStatus } from 'components/shared/StatusHelper';
import FooterButtons from 'components/shared/FooterButtons';
import DrawerFooter from 'components/ui/DrawerFooter';
import TabbedPanel from 'components/shared/TabbedPanel';
import UserForm from './UserForm';
import PermissionForm from './PermissionForm';
import UserFormDrawer from './UserFormDrawer';
import DrawerBody from 'components/ui/DrawerBody';
import AccessiblePropertiesForm from './AccessiblePropertiesForm';

const wStyles = (theme) => ({
  btnRoot: {
    marginLeft: '5px',
    color: '#3383ee',
    border: '2px solid #3383ee',
    '&:hover': {
      border: '2px solid #3383ee',
      backgroundColor: 'rgba(51, 131, 238, 0.1)',
    },
  },
});

const USER_FORM_NAME = 'user_form';
const PROPERTIES_FORM_NAME = 'properties_form';
const PERMISSIONS_FORM_NAME = 'permissions_form';

class UserDetailsOverview extends Component {
  state = {
    showConfirmationDialog: false,
  };

  toggleConfirmationDialog = () => {
    this.setState({
      showConfirmationDialog: !this.state.showConfirmationDialog,
    });
  };

  getUserFullName = () => {
    const formData = this.props.userFormData;
    const { user } = this.props;
    return (
      formData &&
      formData.first_name &&
      formData.last_name &&
      (titleize(`${formData.first_name} ${formData.last_name}`) ||
        titleize(`${user.first_name} ${user.last_name}`))
    );
  };

  sendInvitation = () => {
    const { user } = this.props;
    this.props.sendInvitation(user.user_type, user.user_id);
  };

  sendPasswordReset = () => {
    const { user } = this.props;
    this.props.sendPasswordReset(user.user_type, user.user_id);
  };

  updatePermissions = (formData) => {
    if (formData.role) {
      this.props.change(USER_FORM_NAME, 'role', formData.role);
      this.props.updatePermissions(formData);
    }
  };

  updateUser = (formData) => {
    const role = (this.props.roles || []).filter((role) => role.value === formData.role)[0];
    if (deepDiver(role, 'permissions')) {
      Object.keys(role.permissions).forEach((permissionKey) => {
        this.props.change(PERMISSIONS_FORM_NAME, permissionKey, role.permissions[permissionKey]);
      });
    }
    this.props.change(PERMISSIONS_FORM_NAME, 'role', formData.role);
    this.props.updateUser(formData);
  };

  render() {
    const { user, loggedInUser } = this.props;

    let tabHeaders = [
      getTranslatedString('landlordPortal.users.userDetailsOverview.details'),
      getTranslatedString('landlordPortal.users.userDetailsOverview.assign'),
    ];
    let tabBodies = [this.renderDetailsBody(), this.renderAccessiblePropertiesBody()];
    let tabFooters = [this.renderDetailsFooter(), this.renderPropertiesDetailsFooter()];

    if (featureEnabled('ui.users.new_create_edit_drawer')) {
      tabBodies = [this._renderDetailsBody(), this.renderAccessiblePropertiesBody()];
      tabFooters = [this._renderDetailsFooter(), this.renderPropertiesDetailsFooter()];
    }

    if (user.user_type === 'Agent') {
      if (
        loggedInUser.userType.toLowerCase() !== 'agent' ||
        user.user_id !== loggedInUser.userId ||
        user.admin
      ) {
        tabHeaders.push(
          getTranslatedString('landlordPortal.users.userDetailsOverview.permissions')
        );
        tabBodies.push(this.renderPermissionsBody());
        tabFooters.push(this.renderPermissionsFooter());
      }

      tabHeaders.push(getTranslatedString('landlordPortal.users.userDetailsOverview.availability'));
      tabBodies.push(this.renderAvailabilityBody());
      tabFooters.push(this.renderCloseOnlyFooter());
    }

    return (
      <Fragment>
        <div className="row" style={drawerStyles.tabbedHeaderWrapper}>
          <h3 style={drawerStyles.tabbedHeader}>
            {user.full_name} <UserStatus user={this.props.user} style={{ fontSize: '12px' }} />
          </h3>
        </div>

        <MuiThemeProvider theme={muiThemeNext(AccentColor())}>
          <TabbedPanel tabHeaders={tabHeaders} tabBodies={tabBodies} tabFooters={tabFooters} />
        </MuiThemeProvider>
      </Fragment>
    );
  }

  _renderDetailsBody = () => {
    const { enableSubmit, user, roles, properties } = this.props;
    return (
      <DrawerBody>
        <UserFormDrawer
          enableSubmit={enableSubmit}
          onSubmit={this.updateUser}
          formName={USER_FORM_NAME}
          initialValues={{
            ...user,
            roles: roles,
          }}
          properties={properties}
          user={user}
          toggleConfirmationDialog={this.toggleConfirmationDialog}
        />
        <ConfirmationDialog
          showDialog={this.state.showConfirmationDialog}
          hideDialog={this.toggleConfirmationDialog}
          userConfirmVerbiage={
            user.status === 'Deactivated'
              ? getTranslatedString('landlordPortal.users.userDetailsOverview.reactivate')
              : getTranslatedString('landlordPortal.users.userDetailsOverview.deactivate')
          }
          userConfirmAction={
            user.status === 'Deactivated' ? this.props.reactivateUser : this.props.deactivateUser
          }
          dialogTitle={getTranslatedString('landlordPortal.users.confirmationDialog.title')}
          dialogBodyVerbiage={`${getTranslatedString(
            'landlordPortal.users.confirmationDialog.areYouSure'
          )} ${
            user.status === 'Deactivated'
              ? getTranslatedString('landlordPortal.users.confirmationDialog.reactivateLowercase')
              : getTranslatedString('landlordPortal.users.confirmationDialog.deactivateLowercase')
          } ${getTranslatedString('landlordPortal.users.confirmationDialog.thisUser')}`}
        />
      </DrawerBody>
    );
  };

  renderDetailsBody = () => {
    const targetUser = this.props.user;
    const loggedInUser = this.props.loggedInUser;
    const loggedInIsTarget =
      targetUser.user_type.toLowerCase() === loggedInUser.userType.toLowerCase() &&
      targetUser.user_id === loggedInUser.userId;
    const targetUserIsDeactivated = this.props.user.status === 'Deactivated';

    const showAccountActionButtons = this.showAccountActionButtons();

    return (
      <div style={drawerStyles.tabBodyWrapper}>
        <Card style={drawerStyles.card}>
          {featureEnabled('bounce_emails') && targetUser.bounced_email && (
            <div style={{ textAlign: 'center', paddingTop: '20px' }}>
              <WarningIcon
                size={14}
                style={{
                  marginBottom: '2px',
                  marginRight: '5px',
                  verticalAlign: 'text-bottom',
                  color: colors.PUMPKIN_ORANGE,
                }}
              />
              {getTranslatedString('landlordPortal.users.userDetailsOverview.bouncedEmail', {
                name: `${targetUser.first_name}`,
              })}
            </div>
          )}
          <UserForm
            onSubmit={this.updateUser}
            formName={USER_FORM_NAME}
            initialValues={this.props.user}
            properties={this.props.properties}
            showReset
          />
        </Card>
        <div style={{ textAlign: 'center', paddingBottom: '10px' }}>
          {!loggedInIsTarget && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={this.toggleConfirmationDialog}
              style={{ borderWidth: '2px' }}
            >
              {targetUserIsDeactivated
                ? getTranslatedString('landlordPortal.users.userDetailsOverview.reactivate')
                : getTranslatedString('landlordPortal.users.userDetailsOverview.deactivate')}
            </Button>
          )}
          {showAccountActionButtons &&
            (targetUser.status === 'Invited' || targetUser.status === 'Not Invited') && (
              <Button
                color="primary"
                variant="outlined"
                onClick={this.sendInvitation}
                classes={{ root: this.props.classes.btnRoot }}
              >
                {getTranslatedString('landlordPortal.users.userDetailsOverview.reInvite')}
                <FaEnvelope style={{ fontSize: '21px', marginLeft: '8px' }} />
              </Button>
            )}
          {showAccountActionButtons && targetUser.status === 'Confirmed' && loggedInUser.admin && (
            <Button
              color="primary"
              variant="outlined"
              onClick={this.sendPasswordReset}
              classes={{ root: this.props.classes.btnRoot }}
            >
              {getTranslatedString('landlordPortal.users.userDetailsOverview.sendPasswordReset')}{' '}
              <FaEnvelope style={{ fontSize: '21px', marginLeft: '8px' }} />
            </Button>
          )}
        </div>
        <ConfirmationDialog
          showDialog={this.state.showConfirmationDialog}
          hideDialog={this.toggleConfirmationDialog}
          userConfirmVerbiage={
            targetUserIsDeactivated
              ? getTranslatedString('landlordPortal.users.userDetailsOverview.reactivate')
              : getTranslatedString('landlordPortal.users.userDetailsOverview.deactivate')
          }
          userConfirmAction={
            targetUserIsDeactivated ? this.props.reactivateUser : this.props.deactivateUser
          }
          dialogTitle={getTranslatedString('landlordPortal.users.confirmationDialog.title')}
          dialogBodyVerbiage={`${getTranslatedString(
            'landlordPortal.users.confirmationDialog.areYouSure'
          )} ${
            targetUserIsDeactivated
              ? getTranslatedString('landlordPortal.users.confirmationDialog.reactivateLowercase')
              : getTranslatedString('landlordPortal.users.confirmationDialog.deactivateLowercase')
          } ${getTranslatedString('landlordPortal.users.confirmationDialog.thisUser')}`}
        />
      </div>
    );
  };

  showAccountActionButtons = () => {
    if (!this.props.user.providerIsSSO) return true;
    if (this.props.user.user_type === 'Maintenance') {
      return true;
    }

    return false;
  };

  _renderDetailsFooter = () => {
    return (
      <DrawerFooter
        primaryActionFormName={USER_FORM_NAME}
        primaryActionText={getTranslatedString('landlordPortal.applicantDialog.save')}
        primaryActionDisabled={this.props.disableEditUserSubmit}
        secondaryActionText={getTranslatedString('landlordPortal.users.footer.close')}
        onSecondaryActionClick={this.props.toggleDrawer}
      />
    );
  };
  renderDetailsFooter = () => {
    return (
      <FooterButtons
        submitName={getTranslatedString('landlordPortal.users.footer.save')}
        backName={getTranslatedString('landlordPortal.users.footer.close')}
        formName={USER_FORM_NAME}
        backAction={this.props.toggleDrawer}
        disableSubmitOverride={false}
      />
    );
  };

  renderPropertiesDetailsFooter = () => {
    return (
      <FooterButtons
        submitName={getTranslatedString('landlordPortal.users.footer.save')}
        backName={getTranslatedString('landlordPortal.users.footer.close')}
        formName={PROPERTIES_FORM_NAME}
        backAction={this.props.toggleDrawer}
        disableSubmitOverride={false}
      />
    );
  };

  renderAccessiblePropertiesBody = () => {
    return (
      <div style={drawerStyles.tabBodyWrapper}>
        <Card style={drawerStyles.card}>
          <AccessiblePropertiesForm
            onSubmit={this.updateUser}
            properties={this.props.properties}
            user={this.props.user}
            formName={PROPERTIES_FORM_NAME}
            initialValues={this.props.user}
          />
        </Card>
      </div>
    );
  };

  renderPermissionsBody = () => {
    return (
      <div style={drawerStyles.tabBodyWrapper}>
        <Card style={drawerStyles.card}>
          <PermissionForm
            onSubmit={this.updatePermissions}
            formName={PERMISSIONS_FORM_NAME}
            initialValues={{ ...this.props.user.permissions, role: this.props.user.role }}
          />
        </Card>
      </div>
    );
  };

  renderPermissionsFooter = () => {
    return (
      <FooterButtons
        backName={getTranslatedString('landlordPortal.users.footer.close')}
        submitName={getTranslatedString('landlordPortal.users.footer.save')}
        formName={PERMISSIONS_FORM_NAME}
        backAction={this.props.toggleDrawer}
      />
    );
  };

  renderAvailabilityBody = () => {
    return (
      <div style={drawerStyles.tabBodyWrapper}>
        <Card style={drawerStyles.card}>
          <AvailabilitiesForm
            weekData={this.props.user.availability && this.props.user.availability.availabilities}
            submitAvailability={this.props.updateAvailability}
          />
        </Card>
      </div>
    );
  };

  renderCloseOnlyFooter = () => {
    return (
      <FooterButtons
        hideSubmitButton
        backName={getTranslatedString('landlordPortal.users.footer.close')}
        backAction={this.props.toggleDrawer}
      />
    );
  };
}

const ConnectedUserDetailsOverview = connect(
  (store) => ({
    userFormData: deepDiver(store, `form.${USER_FORM_NAME}.values`),
    loggedInUser: deepDiver(store, 'userTypes'),
    roles: store.landlordUsers.roles,
  }),
  {
    change,
  }
)(UserDetailsOverview);

export default localize()(withStyles(wStyles)(ConnectedUserDetailsOverview));
